import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, from as observableFrom, of as observableOf } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  withLatestFrom,
} from 'rxjs/operators';

import { State, selectBookstores } from '®app/ngrx';
import { BibliographicResourceService } from '®shared/bibliographic-resource';
import { isLoadingOrLoaded } from '®shared/data';

import {
  BookstoreAvailabilityActionTypes,
  BookstoreAvailabilityLoadedAction,
  BookstoreAvailabilityLoadingFailedAction,
  FetchBookstoreAvailabilitiesAction,
  FetchBookstoreAvailabilityAction,
  FetchBookstoreAvailabilityUnlessCachedAction,
} from './bookstore-availability.actions';
import {
  BookstoreAvailabilityState,
  getBookstoreAvailabilityForResourceIdAndBookstoreId,
  selectBookstoreAvailabilityState,
} from './bookstore-availability.state';

@Injectable()
export class BookstoreAvailabilityEffects {
  fetchBookstoreAvailabilities$: Observable<FetchBookstoreAvailabilityUnlessCachedAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(
          BookstoreAvailabilityActionTypes.BOOKSTORE_AVAILABILITIES_REQUESTED,
        ),
        map(
          (action: FetchBookstoreAvailabilitiesAction) =>
            action.payload.resourceId,
        ),
        withLatestFrom(
          this.store.select(selectBookstores),
          (resourceId, bookstores) => ({
            resourceId,
            bookstores,
          }),
        ),
        filter(({ bookstores }) => bookstores && bookstores.length > 0),
        mergeMap(({ resourceId, bookstores }) =>
          observableFrom(
            bookstores
              .map(bookstore => bookstore.id)
              .map(
                bookstoreId =>
                  new FetchBookstoreAvailabilityUnlessCachedAction({
                    resourceId,
                    bookstoreId,
                  }),
              ),
          ),
        ),
      ),
    );

  fetchBookstoreAvailabilityUnlessCached$: Observable<FetchBookstoreAvailabilityAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(
          BookstoreAvailabilityActionTypes.BOOKSTORE_AVAILABILITY_REQUEST_UNLESS_CACHED,
        ),
        // only allow requests where the availability is neither already loaded, nor in the progress of loading
        withLatestFrom(
          this.store.select(selectBookstoreAvailabilityState),
          (
            action: FetchBookstoreAvailabilityUnlessCachedAction,
            state: BookstoreAvailabilityState,
          ) => {
            const { resourceId, bookstoreId } = action.payload;
            const availability =
              getBookstoreAvailabilityForResourceIdAndBookstoreId(
                resourceId,
                bookstoreId,
              )(state);
            return {
              action,
              availability,
            };
          },
        ),
        filter(({ availability }) => !isLoadingOrLoaded(availability)),
        map(
          ({ action }) => new FetchBookstoreAvailabilityAction(action.payload),
        ),
      ),
    );

  fetchBookstoreAvailability$: Observable<
    BookstoreAvailabilityLoadedAction | BookstoreAvailabilityLoadingFailedAction
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(BookstoreAvailabilityActionTypes.BOOKSTORE_AVAILABILITY_REQUESTED),
      mergeMap((action: FetchBookstoreAvailabilityAction) => {
        const { resourceId, bookstoreId } = action.payload;
        return this.bibliographicResourceService
          .getBookstoreAvailabilityForResource(resourceId, bookstoreId)
          .pipe(
            map(
              bookstoreAvailability =>
                new BookstoreAvailabilityLoadedAction({
                  resourceId,
                  bookstoreId,
                  bookstoreAvailability,
                }),
            ),
            catchError(error =>
              observableOf(
                new BookstoreAvailabilityLoadingFailedAction({
                  resourceId,
                  bookstoreId,
                  error,
                }),
              ),
            ),
          );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private bibliographicResourceService: BibliographicResourceService,
  ) {}
}
